import { useEffect, type ReactNode } from 'react';
import { getBridge } from '@/platform';
import { useUiStore } from '@/store/uiStore';

/**
 * Синхронизирует приложение с клиентом мессенджера:
 * тема, высота вьюпорта, безопасные зоны и состояние сети.
 *
 * Все значения кладутся в CSS-переменные, поэтому вёрстка не зависит от React
 * и не «прыгает» при открытии клавиатуры в WebView.
 */
export function PlatformProvider({ children }: { children: ReactNode }) {
  const setTheme = useUiStore((state) => state.setTheme);
  const setOnline = useUiStore((state) => state.setOnline);

  useEffect(() => {
    const bridge = getBridge();
    bridge.ready();

    setTheme(bridge.getColorScheme());
    const unsubscribeTheme = bridge.onColorSchemeChange(setTheme);

    const applyViewport = () => {
      const viewport = bridge.getViewport();
      const height = viewport.height || window.innerHeight;
      document.documentElement.style.setProperty('--bd-viewport-height', `${height}px`);

      const insets = bridge.getSafeAreaInsets();
      if (insets.top) document.documentElement.style.setProperty('--bd-safe-top', `${insets.top}px`);
      if (insets.bottom) document.documentElement.style.setProperty('--bd-safe-bottom', `${insets.bottom}px`);
    };
    applyViewport();
    const unsubscribeViewport = bridge.onViewportChange(applyViewport);
    window.addEventListener('resize', applyViewport);
    window.addEventListener('orientationchange', applyViewport);

    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    return () => {
      unsubscribeTheme();
      unsubscribeViewport();
      window.removeEventListener('resize', applyViewport);
      window.removeEventListener('orientationchange', applyViewport);
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [setTheme, setOnline]);

  return <>{children}</>;
}
